import { useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import AnnouncementForm, {
	AnnouncementFormValues
} from "../../components/AnnouncementForm/AnnouncementForm";
import AnnouncementAnalytics from "../../components/AnnouncementAnalytics/AnnouncementAnalytics";
import AnnouncementPreview from "../../components/AnnouncementPreview/AnnouncementPreview";
import PushPreviewPanel from "../../components/PushPreviewPanel/PushPreviewPanel";
import useAnnouncement from "../../hooks/useAnnouncement";
import useEditAnnouncement from "../../hooks/useEditAnnouncement";
import useDeleteAnnouncement from "../../hooks/useDeleteAnnouncement";
import useSendAnnouncement from "../../hooks/useSendAnnouncement";
import useScheduleAnnouncement from "../../hooks/useScheduleAnnouncement";
import useRevertToDraft from "../../hooks/useRevertToDraft";
import { adaptEditAnnouncementPayload } from "../../adapters/adaptAnnouncementPayload";
import { formValuesToAnnouncement } from "../../adapters/formValuesToAnnouncement";
import useAnnouncementAnalytics from "../../hooks/useAnnouncementAnalytics";
import {
	announcementToFormValues,
	contentBlocksToBlocks,
	formatAudience,
	parseBodyToBlocks
} from "../../utils/announcementFormHelpers";

function formatDate(iso: string | null): string {
	if (!iso) return "\u2014";
	const date = new Date(iso);
	if (!Number.isFinite(date.getTime())) return "\u2014";
	return date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
}

const EditAnnouncement = () => {
	const navigate = useNavigate();
	const { announcementId } = useParams<{ announcementId: string }>();
	const id = Number(announcementId);
	const isValidId = Number.isInteger(id) && id > 0;

	const {
		data: announcement,
		isLoading,
		error: announcementError
	} = useAnnouncement(isValidId ? id : 0);

	const { mutate: editAnnouncement, isLoading: isEditing } =
		useEditAnnouncement();
	const { mutate: deleteAnnouncement, isLoading: isDeleting } =
		useDeleteAnnouncement();
	const { mutate: sendAnnouncement, isLoading: isSending } =
		useSendAnnouncement();
	const { mutate: scheduleAnnouncement, isLoading: isScheduling } =
		useScheduleAnnouncement();
	const { mutate: revertToDraft, isLoading: isReverting } = useRevertToDraft();

	const isEditable =
		announcement?.status === "draft" || announcement?.status === "scheduled";
	const editingScheduled = announcement?.status === "scheduled";
	const { data: analytics = null } = useAnnouncementAnalytics(
		id,
		isValidId && !isEditable && !!announcement
	);

	const [formDefaults, setFormDefaults] =
		useState<AnnouncementFormValues | null>(null);
	const [existingImageReference, setExistingImageReference] = useState<
		string | null
	>(null);

	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [confirmKind, setConfirmKind] = useState<"send" | "schedule" | null>(
		null
	);
	const [pendingData, setPendingData] = useState<{
		data: AnnouncementFormValues;
		imageRemoved: boolean;
	} | null>(null);

	useEffect(() => {
		if (announcement) {
			setFormDefaults(announcementToFormValues(announcement));
			setExistingImageReference(announcement.heroImage ?? null);
		}
	}, [announcement]);

	if (!isValidId) {
		return <Navigate to="/announcements" replace />;
	}

	if (
		!isLoading &&
		announcementError &&
		(announcementError as AxiosError)?.response?.status === 404
	) {
		return <Navigate to="/announcements" replace />;
	}

	if (isLoading || !announcement) {
		return (
			<Page>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<p className="text-[13px] text-muted-foreground">Loading...</p>
				</div>
			</Page>
		);
	}

	// Editable mode handlers
	const handleSaveDraft = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		const announcementData = formValuesToAnnouncement(data, id);
		const payload = adaptEditAnnouncementPayload(
			announcementData,
			data.heroImage,
			imageRemoved
		);
		editAnnouncement(
			{ id, payload },
			{
				onSuccess: () => navigate("/announcements")
			}
		);
	};

	const handleSendNow = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		setPendingData({ data, imageRemoved });
		setConfirmKind("send");
	};

	const handleSchedule = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		setPendingData({ data, imageRemoved });
		setConfirmKind("schedule");
	};

	const closeConfirm = () => {
		setConfirmKind(null);
		setPendingData(null);
	};

	const confirmSubmit = () => {
		if (!pendingData || !confirmKind) return;
		const { data, imageRemoved } = pendingData;
		const announcementData = formValuesToAnnouncement(data, id);
		const payload = adaptEditAnnouncementPayload(
			announcementData,
			data.heroImage,
			imageRemoved
		);
		editAnnouncement(
			{ id, payload },
			{
				onSuccess: () => {
					if (confirmKind === "send") {
						sendAnnouncement(id, {
							onSuccess: () => {
								closeConfirm();
								navigate("/announcements");
							},
							onError: closeConfirm
						});
					} else {
						scheduleAnnouncement(id, {
							onSuccess: () => {
								closeConfirm();
								navigate("/announcements");
							},
							onError: closeConfirm
						});
					}
				},
				onError: closeConfirm
			}
		);
	};

	const handleRevertToDraft = () => {
		revertToDraft(id);
	};

	const confirmDelete = () => {
		deleteAnnouncement(id, {
			onSuccess: () => {
				setShowDeleteConfirm(false);
				navigate("/announcements");
			}
		});
	};

	const getSendConfirmMessage = () => {
		if (!pendingData) return "";
		const { data } = pendingData;
		const audience =
			data.audienceType === "everyone"
				? "all users"
				: data.audienceType === "platform"
					? `${data.audiencePlatform === "both" ? "all" : data.audiencePlatform} users`
					: data.audienceType === "groups"
						? `${data.audienceGroupIds.length} selected group${data.audienceGroupIds.length !== 1 ? "s" : ""}`
						: `${data.audienceUserIds.length} selected user${data.audienceUserIds.length !== 1 ? "s" : ""}`;

		if (confirmKind === "schedule") {
			return `Schedule this announcement for ${data.scheduledDate} at ${data.scheduledTime || "00:00"} to ${audience}?`;
		}
		return `Send this announcement now to ${audience}?`;
	};

	// Editable view
	if (isEditable) {
		const isBusy = isEditing || isSending || isScheduling || isReverting;
		return (
			<Page>
				{formDefaults && (
					<AnnouncementForm
						defaultValues={formDefaults}
						existingImageReference={existingImageReference}
						onSubmit={handleSaveDraft}
						onSchedule={handleSchedule}
						onSendNow={handleSendNow}
						onDelete={() => setShowDeleteConfirm(true)}
						onRevertToDraft={editingScheduled ? handleRevertToDraft : undefined}
						isEdit
						editingScheduled={editingScheduled}
						isSubmitting={isEditing}
						isScheduling={isScheduling}
						isSending={isSending}
						isDeleting={isDeleting || isReverting}
					/>
				)}

				<ConfirmationModal
					isOpen={confirmKind !== null}
					title={
						confirmKind === "schedule"
							? "Schedule announcement?"
							: "Send announcement?"
					}
					description={getSendConfirmMessage()}
					onCancel={() => {
						if (!isBusy) closeConfirm();
					}}
					cancelBtnProps={{ disabled: isBusy }}
					onSubmit={confirmSubmit}
					submitBtnProps={{ isLoading: isBusy }}
				/>

				<ConfirmationModal
					isOpen={showDeleteConfirm}
					title="Delete announcement?"
					description="This action cannot be undone."
					onCancel={() => {
						if (!isDeleting) setShowDeleteConfirm(false);
					}}
					cancelBtnProps={{ disabled: isDeleting }}
					onSubmit={confirmDelete}
					submitBtnProps={{ isLoading: isDeleting }}
				/>
			</Page>
		);
	}

	// Read-only view (sent / expired)
	const statusBadge =
		announcement.status === "sent" ? (
			<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700">
				Sent
			</span>
		) : (
			<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-50 text-red-700">
				Expired
			</span>
		);

	const sentDate = announcement.sentAt
		? new Date(announcement.sentAt).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "long",
				year: "numeric"
			})
		: null;

	const previewBlocks =
		announcement.contentBlocks && announcement.contentBlocks.length > 0
			? contentBlocksToBlocks(announcement.contentBlocks)
			: parseBodyToBlocks(announcement.body, undefined);

	// Mirror `announcementToFormValues` — a record without a body and
	// without meaningful structured content blocks was sent as a
	// push-only notification, so the in-app card has nothing useful
	// to show. Render the push preview instead so admins see what
	// actually landed on devices rather than an empty "announcement"
	// placeholder.
	//
	// `previewBlocks` always has at least one entry (parseBodyToBlocks
	// returns a single empty-text-block when body is null), so we look
	// at the source data rather than the parsed array. We also peek
	// inside each block — a single placeholder text block with empty
	// content is the same "no in-app content" state as a missing
	// blocks array, and treating it as in-app content would re-introduce
	// the empty-card regression this view is meant to fix.
	const blockHasContent = (b: { type: string; content?: string }) => {
		if (b.type === "text") return !!b.content?.trim();
		// image / video blocks store a blob reference in `content`.
		return !!b.content?.trim();
	};
	const hasInAppContent =
		!!announcement.body?.trim() ||
		(announcement.contentBlocks ?? []).some(blockHasContent);
	const isPushOnly = !hasInAppContent;
	const headerLabel =
		announcement.title?.trim() ||
		announcement.pushTitle?.trim() ||
		"Untitled announcement";

	return (
		<Page>
			<div className="space-y-4">
				{/* Status bar */}
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<div className="flex items-start justify-between gap-4">
						<div className="min-w-0 flex-1">
							{/* Heading element (not <label>) so screen readers
							    expose this in the page's heading outline and
							    keyboard users can navigate to it via heading
							    shortcuts. The previous <label> had no
							    associated form control, which AT would either
							    skip or announce as an unlabelled control. */}
							<h2 className="font-heading text-lg font-medium text-foreground block mb-1">
								{headerLabel}
							</h2>
							<p className="text-[13px] text-muted-foreground">
								This {isPushOnly ? "push notification" : "announcement"} has
								been {announcement.status} and cannot be edited.
							</p>
						</div>
						{statusBadge}
					</div>
				</div>

				{isPushOnly ? (
					<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
						<h3 className="font-heading text-lg font-medium text-foreground mb-1">
							Push notification
						</h3>
						<p className="text-[13px] text-muted-foreground mb-4">
							The exact content delivered to recipients&apos; devices.
						</p>
						<PushPreviewPanel
							pushTitle={announcement.pushTitle ?? ""}
							pushBody={announcement.pushBody ?? ""}
						/>
					</div>
				) : (
					<AnnouncementPreview
						title={announcement.title}
						heroImageRef={announcement.heroImage}
						blocks={previewBlocks}
						author={
							announcement.createdBy
								? {
										firstName: announcement.createdBy.firstName,
										lastName: announcement.createdBy.lastName
									}
								: null
						}
						dateLabel={sentDate}
					/>
				)}

				{/* Metadata grid */}
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<label className="font-heading text-lg font-medium text-foreground mb-4 block">
						Details
					</label>
					<dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4">
						<div>
							<dt className="text-sm font-medium text-zinc-600">Sent</dt>
							<dd className="mt-1 text-sm text-foreground">
								{formatDate(announcement.sentAt)}
							</dd>
						</div>
						<div>
							<dt className="text-sm font-medium text-zinc-600">Audience</dt>
							<dd className="mt-1 text-sm text-foreground">
								{formatAudience(announcement)}
							</dd>
						</div>
						<div>
							<dt className="text-sm font-medium text-zinc-600">Expiry</dt>
							<dd className="mt-1 text-sm text-foreground">
								{formatDate(announcement.expiresAt)}
							</dd>
						</div>
						<div>
							<dt className="text-sm font-medium text-zinc-600">Created by</dt>
							<dd className="mt-1 text-sm text-foreground">
								{announcement.createdBy
									? `${announcement.createdBy.firstName} ${announcement.createdBy.lastName}`
									: "\u2014"}
							</dd>
						</div>
					</dl>
				</div>

				{/* Analytics */}
				{analytics && <AnnouncementAnalytics analytics={analytics} />}

				{/* Back link */}
				<div className="pt-2">
					<button
						type="button"
						onClick={() => navigate("/announcements")}
						className="flex items-center gap-2 text-sm text-zinc-600 hover:text-foreground transition-colors"
					>
						<ArrowLeft size={16} />
						Back to Announcements
					</button>
				</div>
			</div>
		</Page>
	);
};

export default EditAnnouncement;
